import { MeasureAbsorbManager } from '@/editor/manager/absorb/measure/measureAbsorbManager';
import { TGlobalStores } from '@/editor/stores';
import { CommonController2d } from '../CommonController';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { clientPosToWorldPos } from '@/editor/utils';
import { LinearDimensionModel } from '@/editor/entities/2d/linearDimensionModel';
import { createLinearDimension, ILinearDimension } from '@/editor/data/dimension/dimension';
import editorEvent, { EditorEventName } from '@/editor/event/editorEvent';

export class DrawLinearDimension extends CommonController2d {
  private readonly _mouseButton = { LEFT: 0, RIGHT: 2 } as const;
  private _points: IPoint2d[] = [];
  private _movePoints: IPoint2d | null = null;
  private _absorbManager: MeasureAbsorbManager;
  private _virtualLinearDimension: Omit<ILinearDimension, 'id'> | null = null;
  private _virtualModel: LinearDimensionModel = new LinearDimensionModel({});
  private _disposeFunc: () => void;

  constructor(gs: TGlobalStores) {
    super(gs);
    this._absorbManager = new MeasureAbsorbManager(gs);
    this._absorbManager.updateBaseData();
    this._absorbManager.updateLinearDimensionLineInfos();
    this._disposeFunc = editorEvent.on(EditorEventName.ZOOM_UPDATE_2D, this._updateVirtualModelScale);
  }

  private _updateVirtualModelScale = (scale: number) => {
    if (this._virtualLinearDimension) this._virtualModel.updateScale(this._virtualLinearDimension, 100 / scale);
  };

  private _updateVirtualModel() {
    if (this._virtualLinearDimension) this._virtualModel.updateModel(this._virtualLinearDimension);
  }

  private _safePushPoint(point: IPoint2d) {
    if (this._points.length === 3) return;
    if (!this._points.length) {
      this._points.push(Point2dUtils.clone(point));
      return;
    }
    const lastPoint = this._points[this._points.length - 1];
    if (!Point2dUtils.equals(lastPoint, point)) {
      this._points.push(Point2dUtils.clone(point));
    }
  }

  private _safePopPoint() {
    if (this._points.length) {
      const point = this._points.pop();
      return point;
    }

    return undefined;
  }

  private _clickMouseLeftHandler() {
    if (this._movePoints) {
      this._safePushPoint(this._movePoints);
      this._tryCompleteDimension();
    }
  }

  private _clickMouseRightHandler() {
    const lastPoint = this._safePopPoint();
    if (!lastPoint) {
      this.dispose();
      this.exit();
    } else if (!this._points.length) {
      this.clear();
    }
  }

  private _tryCompleteDimension() {
    if (this._points.length < 3) {
      this._virtualLinearDimension = LinearDimensionModel.convertPointsToLinearDimension(this._points);
      this._updateVirtualModel();
      return;
    }
    // 满足三点，新增数据
    this._gs.dimensionOperation.addLinearDimension(createLinearDimension(this._points));
    this._points = [];
    this._virtualLinearDimension = null;
    this._virtualModel.clear();
    // 完成后更新距离标注吸附数据
    this._absorbManager.updateLinearDimensionLineInfos();
  }

  private _getWorldPositionFromMouseEvent(e: MouseEvent) {
    const {
      renderer,
      scene2d: { camera }
    } = this._gs.editorStateStore.sceneInfo;
    const worldPosition = clientPosToWorldPos(e, camera, renderer);

    return worldPosition;
  }

  protected override mouseDown(_e: MouseEvent): void {}

  protected override mouseMove(e: MouseEvent): void {
    const point = this._getWorldPositionFromMouseEvent(e);
    const absorbPoint =
      this._points.length === 2
        ? this._absorbManager.calculateAbsorbPointByLinearThenRender(this._points[0], this._points[1], point)
        : this._absorbManager.calculateAbsorbPointThenRender(point);

    this._movePoints = Point2dUtils.clone(absorbPoint ?? point);
    if (this._points.length && this._points.length < 3) {
      this._virtualLinearDimension = LinearDimensionModel.convertPointsToLinearDimension([
        ...this._points,
        this._movePoints
      ]);
      this._updateVirtualModel();
    }
  }

  protected override mouseUp(e: MouseEvent, dragged: boolean): void {
    if (dragged) return;

    if (e.button === this._mouseButton.LEFT) {
      this._clickMouseLeftHandler();
    }

    if (e.button === this._mouseButton.RIGHT) {
      this._clickMouseRightHandler();
    }
  }

  clear() {
    this._points = [];
    this._virtualLinearDimension = null;
    this._absorbManager.clearModel();
    this._virtualModel.clear();
  }

  override dispose(): void {
    this._disposeFunc();
    this.clear();
    this._virtualModel.dispose();
    super.dispose();
  }
}
